import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, HardHat } from "lucide-react";

/**
 * Handles the OAuth callback from Emergent-managed Google Auth.
 * Runs BEFORE ProtectedRoute / AuthProvider auth check so it can establish the session first.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        // Clear the hash so refresh doesn't reprocess
        window.history.replaceState(null, "", window.location.pathname);
        await refresh();
        toast.success(`Welcome, ${data.name || data.email}`);
        nav(data.onboarded ? "/" : "/onboarding", { replace: true });
      } catch (e) {
        const detail = e.response?.data?.detail || "Sign-in failed. Please try again.";
        toast.error(typeof detail === "string" ? detail : "Sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, nav, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1F1F1F] bi-grain px-4" data-testid="auth-callback">
      <div className="relative z-10 text-center">
        <div className="w-14 h-14 rounded-xl bg-[#FF9500] mx-auto flex items-center justify-center text-[#111827]">
          <HardHat className="w-7 h-7" />
        </div>
        <div className="mt-4 font-heading font-extrabold text-2xl text-white">Signing you in…</div>
        <div className="mt-2 text-white/60 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Handing you off to your BuildIntel workspace
        </div>
      </div>
    </div>
  );
}
